import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import Turnstile from '../components/Turnstile';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2452ebe5-8b88-4541-a707-a9ae800f68db/artifacts/oj0fg93b_ChatGPT%20Image%2011%20mrt%202026%2C%2015_21_20.png";

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    // Check Turnstile verification
    if (!turnstileToken) {
      toast.error('Please complete the verification challenge');
      return;
    }

    setLoading(true);
    try {
      await login(formData.email, formData.password, turnstileToken);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
      // Reset turnstile on error
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-800 relative overflow-hidden" data-testid="login-page">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
      
      {/* Content */}
      <div className="container-custom py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="Elementary Cargo" 
                className="h-20 mx-auto mb-4"
              />
            </Link>
            <h1 className="font-outfit text-2xl font-bold text-white mb-2">{t('login.title')}</h1>
            <p className="text-steel-400">{t('login.subtitle')}</p>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div>
                  <Label className="text-navy-800">{t('login.email')}</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      className="pl-10"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('login.password')}</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder={t('login.passwordPlaceholder')}
                      className="pl-10 pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-steel-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-steel-300" />
                    <span className="text-sm text-steel-600">{t('login.rememberMe')}</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-gold hover:underline">
                    {t('login.forgotPassword')}
                  </Link>
                </div>

                {/* Cloudflare Turnstile CAPTCHA */}
                <div className="flex justify-center">
                  <Turnstile
                    onVerify={(token) => setTurnstileToken(token)}
                    onError={() => {
                      setTurnstileToken(null);
                      toast.error('Verification failed. Please try again.');
                    }}
                    onExpire={() => setTurnstileToken(null)}
                    theme="light"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-accent w-full" 
                  disabled={loading || !turnstileToken}
                  data-testid="login-submit-btn"
                >
                  {loading ? t('login.signingIn') : t('login.signIn')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>

              <div className="mt-6 text-center space-y-3">
                <p className="text-steel-500 text-xs">
                  {t('admin.accessDeniedNote')}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
